import React from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/styles";
import { useLocation } from "react-router-dom";
import useTranslation from "../../hooks/useTranslation";

const useStyles = (theme, isMobile) => ({
  container: {
    width: "100%",
    display: "flex",
    flexDirection: isMobile ? "row" : "column",
    justifyContent: "center",
    alignItems: "center",
    margin: "100px 0px",
  },
  titleContainer: {
    marginTop: isMobile ? "110.4px" : "262.3px",
    display: { md: "flex" },
    justifyContent: { md: "center" },
    alignItems: { md: "center" },
  },
  line: {
    width: "25px",
    border: `1px solid ${theme.palette.red.main}`,
    marginRight: "8px",
    backgroundColor: theme.palette.red.main,
    marginBottom: "3rem",
  },
  card: {
    width: { xs: "299px", md: "338px", lg: "357.5px", xl: "418px" },
    height: isMobile ? "410px" : { xs: "410px", md: "411.5px", xl: "454px" },
    position: isMobile ? "static" : "relative",
    top: "-70px",
    borderRadius: "47px",
    margin: isMobile ? "33px 0px" : "0 50px",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    background:
      "linear-gradient(to top, #23213f, #1d1f3e, #171c3e, #0e1a3e, #03183d)",
    color: theme.palette.white.main,
    boxShadow: "0 10px 50px 0 rgba(0, 0, 0, 0.05);",
  },
  secondCard: {
    width: { xs: "299px", md: "338px", lg: "357.5px", xl: "418px" },
    height: { xs: "410px", md: "411.5px", xl: "454px" },
    borderRadius: "47px",
    margin: "33px 0px",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    bgcolor: theme.palette.white.main,
    color: theme.palette.blue.main,
    boxShadow: "0 10px 50px 0 rgba(0, 0, 0, 0.05);",
  },
  typography: {
    descriptionSubtitle: {
      fontSize: isMobile ? 14 : 16,
      fontWeight: "bold",
      letterSpacing: "2px",
      color: theme.palette.red.main,
      marginBottom: "3rem",
    },
    section: {
      fontSize: isMobile ? "14px" : "16px",
      fontWeight: "bold",
      letterSpacing: "2px",
      color: theme.palette.red.main,
      marginTop: isMobile ? "59px" : "0",
    },
    descriptionTitle: {
      fontSize: isMobile ? 32 : 65,
      fontWeight: "lighter",
      fontFamily: theme.typography.montserrat.regular,
      color: theme.palette.blue.main,
      lineHeight: "1.5rem",
      marginBottom: "1rem",
    },
    title: {
      color: theme.palette.blue.main,
      fontFamily: theme.typography.montserrat.bold,
      fontSize: isMobile ? "30px" : { xl: "30px", lg: "24px", md: "19.2px" },
      marginTop: isMobile ? "31.4px" : "38px",
      maxWidth: isMobile ? "252px" : { xl: "370px", lg: "296px", md: "200px" },
    },
    text: {
      fontFamily: theme.typography.anaHeim.regular,
      fontSize: isMobile ? "24px" : { xl: "24px", lg: "19.2px", md: "15.36" },
      marginTop: isMobile ? "7px" : "20px",
      maxWidth: isMobile
        ? "260px"
        : { xl: "328px", lg: "262.4px", md: "209.9px" },
    },
  },
});

const BenefitsSection = () => {
  const isMobile = useMediaQuery("(max-width:1140px)");
  const theme = useTheme();
  const classes = useStyles(theme, isMobile);
  const { pathname } = useLocation();
  const { title, description, group, learn, remote } = useTranslation(
    pathname,
    "BenefitsSection"
  );

  return isMobile ? (
    <Box>
      <Typography sx={classes.typography.descriptionSubtitle}>
        {title}
      </Typography>
      <Typography sx={classes.typography.descriptionTitle}>
        <b>{description[0]}</b>
      </Typography>
      <Typography sx={classes.typography.descriptionTitle}>
        {description[1]}
      </Typography>
      <Box sx={classes.card}>
        <img
          src={group.img.src}
          alt={group.img.alt}
          style={{ height: "85.6px", marginTop: "50px" }}
        />
        <Typography
          sx={{ ...classes.typography.title, color: theme.palette.red.main }}
        >
          {group.title}
        </Typography>
        <Typography sx={classes.typography.text}>
          {group.description}
        </Typography>
      </Box>
      <Box sx={classes.secondCard}>
        <img
          src={learn.img.src}
          alt={learn.img.alt}
          style={{ height: "57px", marginTop: "50px" }}
        />
        <Typography sx={classes.typography.title}>{learn.title}</Typography>
        <Typography sx={classes.typography.text}>
          {learn.description}
        </Typography>
      </Box>
      <Box sx={classes.secondCard}>
        <img
          src={remote.img.src}
          alt={remote.img.alt}
          style={{ height: "73px", marginTop: "25px" }}
        />
        <Typography sx={{ ...classes.typography.title, marginTop: "0px" }}>
          {remote.title}
        </Typography>
        <Typography sx={classes.typography.text}>
          {remote.description}
        </Typography>
      </Box>
    </Box>
  ) : (
    <Box sx={classes.container}>
      <Box sx={classes.titleContainer}>
        <div style={classes.line}></div>
        <Typography sx={classes.typography.descriptionSubtitle}>
          {title}
        </Typography>
      </Box>
      <Box sx={classes.description}>
        <Typography sx={classes.typography.descriptionTitle}>
          <b>{description[0]}</b>
          {description[1]}
        </Typography>
      </Box>
      <Box style={{ display: "flex", marginTop: "140px" }}>
        <Box sx={classes.secondCard}>
          <img
            src={learn.img.src}
            alt={learn.img.alt}
            style={{ height: "73px", marginTop: "68px" }}
          />
          <Typography sx={classes.typography.title}>{learn.title}</Typography>
          <Typography sx={classes.typography.text}>
            {learn.description}
          </Typography>
        </Box>
        <Box sx={classes.card}>
          <img
            src={group.img.src}
            alt={group.img.alt}
            style={{ height: "73px", marginTop: "68px" }}
          />
          <Typography
            sx={{
              ...classes.typography.title,
              color: theme.palette.red.main,
            }}
          >
            {group.title}
          </Typography>
          <Typography sx={classes.typography.text}>
            {group.description}
          </Typography>
        </Box>
        <Box sx={classes.secondCard}>
          <img
            src={remote.img.src}
            alt={remote.img.alt}
            style={{ height: "73px", marginTop: "68px" }}
          />
          <Typography sx={classes.typography.title}>{remote.title}</Typography>
          <Typography sx={classes.typography.text}>
            {remote.description}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default BenefitsSection;
